/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Aug 02 2024
 **/

import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { useStompClient } from "@/frameworks/libraries/network";

import { useBlock, useCall, useFriend } from "@/frameworks/core/API";

export type FriendUpdate =
	| { type: "add" | "delete"; payload: { userID: string } }
	| { type: "bookmark"; payload: { userID: string; active: boolean } }
	| { type: "block"; payload: { userID: string; active: boolean } };

export default function useFriendListActions() {
	const friendAPI = useFriend();
	const callAPI = useCall();
	const blockAPI = useBlock();

	const load = useCallback(async () => {
		const data = (await friendAPI.friends.execute()).data;

		if (!data) return;

		const friendList: Map<string, WebServices.Friend.Friends.Item> = new Map();

		data?.forEach((item) => {
			friendList.set(item.userId, item);
		});

		return friendList;
	}, []);

	const blockListLoad = useCallback(async () => {
		const data = (await blockAPI.list.execute()).data;

		if (!data) return;

		const blockList: Map<string, WebServices.Block.List.Item> = new Map();

		data?.forEach((item) => {
			blockList.set(item.userId, item);
		});

		return blockList;
	}, []);

	const {
		data: friendList,
		isLoading,
		refetch: getFriendList
	} = useQuery({
		queryKey: ["getFriendList"],
		queryFn: async () => {
			const result: Map<string, WebServices.Friend.Friends.Item> = new Map();

			const data = (await friendAPI.friends.execute()).data;

			data?.forEach((item) => {
				result.set(item.userId, item);
			});

			return result;
		}
	});

	const {
		data: blockList,
		isLoading: isBlockLoading,
		refetch: getBlockUserList
	} = useQuery({
		queryKey: ["getBlockUserList"],
		queryFn: async () => {
			const result: Map<string, WebServices.Block.List.Item> = new Map();
			const data = (await blockAPI.list.execute()).data;

			data?.forEach((item) => {
				result.set(item.userId, item);
			});

			return result;
		}
	});

	const update = async ({ type, payload }: FriendUpdate) => {
		switch (type) {
			case "add":
				return await friendAPI.add.execute({ userId: payload.userID });
			case "delete":
				return await friendAPI.remove.execute({ userId: payload.userID });
			case "block":
				if (payload.active) {
					return await blockAPI.request.execute({ userId: payload.userID });
				} else {
					return await blockAPI.release.execute({ userId: payload.userID });
				}
			case "bookmark":
				if (payload.active) {
					return await callAPI.favoriteAdd.execute({ targetUserId: payload.userID });
				} else {
					return await callAPI.favoriteDelete.execute({ targetUserId: payload.userID });
				}
			default:
				break;
		}
	};

	const { data: newFriendChecked, refetch: getNewFriendChecked } = useQuery({
		queryKey: ["newFriendChecked"],
		queryFn: async () => (await friendAPI.new.execute()).data?.exists,
		enabled: false
	});

	useStompClient({
		onMessageReceived(message) {
			const messageType = message.type as PushServices.People.MessageIDs;

			switch (messageType) {
				case "friendRequested":
					getNewFriendChecked();
					break;
				default:
					break;
			}
		}
	});

	return {
		load,
		blockListLoad,
		isLoading,
		isBlockLoading,
		update,
		friendList,
		blockList,
		getFriendList,
		getBlockUserList,
		newFriendChecked,
		getNewFriendChecked
	};
}
